import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import type { SurveyQuestion, TripleResponseAnswer } from '@/types/survey';

interface Props {
  question: SurveyQuestion;
  answer: TripleResponseAnswer | undefined;
  onChange: (ans: TripleResponseAnswer) => void;
}

export const TripleResponseQuestion: React.FC<Props> = ({ question, answer, onChange }) => {
  const subQs = question.sub_questions ?? [];

  const values = useMemo(() => answer?.sub_answers ?? {}, [answer?.sub_answers]);

  const handleChange = useCallback(
    (subId: string, value: string, maxLen?: number) => {
      const nextVal = typeof maxLen === 'number' ? value.slice(0, maxLen) : value;
      const next: TripleResponseAnswer = {
        question_id: question.id,
        question_type: 'triple_response',
        sub_answers: { ...values, [subId]: nextVal },
      };
      onChange(next);
    },
    [question.id, values, onChange]
  );

  if (!subQs.length) {
    return <div className='text-gray-600'>No sub-questions provided.</div>;
  }

  return (
    <div className='flex flex-col gap-4'>
      {subQs.map((sq) => {
        const val = values[sq.id] ?? '';
        const maxLen = typeof sq.max_length === 'number' ? sq.max_length : 500;
        return (
          <div key={sq.id} className='flex flex-col gap-2'>
            <label className='font-sans text-14 font-medium text-gray-800'>{sq.prompt}</label>
            <textarea
              value={val}
              onChange={(e) => handleChange(sq.id, e.target.value, maxLen)}
              placeholder={sq.placeholder || 'Type your response...'}
              maxLength={maxLen}
              className={clsx(
                'min-h-[100px] w-full resize-y rounded-12 border border-gray-300 bg-white p-3',
                'font-sans text-[15px] leading-normal text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none'
              )}
            />
            <div className='text-right text-xs text-gray-600'>
              {val.length}/{maxLen}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TripleResponseQuestion;
